// ============================================
// スコア・ウェーブ・コンボ・敵カウンター表示
// ============================================

import p5 from 'p5';
import { getP5 } from '../../core/p5-instance';
import { CONFIG } from '../../config/game-config';
import { getScore, getWave, getCombo, getHighScore, getRank, setRank, getWaveEnemiesDefeated, getStageNameDisplayTimer } from '../../store/index';
import { getCurrentStageCfg, getLeftPanelCenterX, getRightPanelCenterX, getEnemiesRequiredForWave, calculateRank, getRankColor } from '../../utils/helpers';
import {
    getLastAnimatedCombo,
    setLastAnimatedCombo,
    getComboAnimTimer,
    setComboAnimTimer
} from '../../store/animation-state';
import { HUD_COLORS } from '../../config/hud-colors';

// ============================================
// HUD レイアウト定数（左右パネル用）
// ============================================

const LABEL_SIZE = 11;
const VALUE_SIZE = 18;
const SMALL_VALUE_SIZE = 14;

// 左パネル Y座標
const SCORE_LABEL_Y = 20;
const SCORE_VALUE_Y = 35;
const RANK_Y = 56;
const HI_LABEL_Y = 72;
const HI_VALUE_Y = 85;
const STAGE_LABEL_Y = 115;
const STAGE_VALUE_Y = 130;
const WAVE_LABEL_Y = 155;
const WAVE_VALUE_Y = 170;
const ENEMY_COUNTER_Y = 215;

// 右パネル コンボ Y座標
const COMBO_LABEL_Y = 280;
const COMBO_VALUE_Y = 300;
const COMBO_BASE_SIZE = 24;

// ============================================
// コンボアニメーション定数
// ============================================

const COMBO_ANIM_DURATION = 20;
const COMBO_ANIM_INTERVAL = 10;
const COMBO_ANIM_MAJOR_INTERVAL = 50;
const COMBO_ANIM_THRESHOLD = 10;
const COMBO_RAINBOW_THRESHOLD = 200;

// コンボ途切れ演出定数
const COMBO_BREAK_DURATION = 30;
let _prevCombo = 0;
let _comboBreakTimer = 0;
let _brokenComboValue = 0;

// グローレイヤー定義: [サイズ倍率, アルファ倍率]
const NORMAL_GLOW_LAYERS: [number, number][] = [
    [1.15, 100],
    [1.08, 180],
    [1.0, 255]
];
const MAJOR_GLOW_LAYERS: [number, number][] = [
    [1.3, 80],
    [1.2, 150],
    [1.1, 220],
    [1.0, 255]
];

// ============================================
// 色定義
// ============================================

type RGB = { r: number; g: number; b: number };

const COLOR_WHITE: RGB = { r: 255, g: 255, b: 255 };

/** コンボ数に応じた色閾値テーブル（降順で評価） */
const COMBO_COLOR_THRESHOLDS: { min: number; color: RGB }[] = [
    { min: 150, color: { r: 255, g: 215, b: 0 } },   // ゴールド
    { min: 100, color: { r: 255, g: 50, b: 150 } },  // マゼンタ
    { min: 75, color: { r: 255, g: 50, b: 80 } },    // レッド
    { min: 50, color: { r: 255, g: 100, b: 50 } },   // 赤オレンジ
    { min: 35, color: { r: 255, g: 180, b: 50 } },   // オレンジ
    { min: 20, color: { r: 255, g: 255, b: 80 } },   // イエロー
    { min: 10, color: { r: 150, g: 255, b: 100 } },  // ライム
    { min: 5, color: { r: 50, g: 220, b: 180 } },    // エメラルド
    { min: 0, color: { r: 100, g: 200, b: 255 } },   // シアン
];

// ============================================
// ユーティリティ関数
// ============================================

/** スコアをカンマ区切りでフォーマット */
function formatScore(score: number): string {
    return Math.floor(score).toLocaleString('en-US');
}

/** 虹色を取得（色相を時間で回転） */
function getRainbowColor(p: p5): RGB {
    const hue = (p.frameCount * 3) % 360;
    p.colorMode(p.HSB, 360, 100, 100);
    const c = p.color(hue, 100, 100);
    p.colorMode(p.RGB, 255);
    return { r: p.red(c), g: p.green(c), b: p.blue(c) };
}

/** コンボ数に応じた色を取得 */
function getComboColor(p: p5, combo: number): RGB {
    if (combo >= COMBO_RAINBOW_THRESHOLD) {
        return getRainbowColor(p);
    }
    return COMBO_COLOR_THRESHOLDS.find(t => combo >= t.min)?.color
        ?? COMBO_COLOR_THRESHOLDS[COMBO_COLOR_THRESHOLDS.length - 1].color;
}

/** グロー効果を描画 */
function drawGlowEffect(
    p: p5,
    text: string,
    x: number,
    y: number,
    baseSize: number,
    glowIntensity: number,
    isMajor: boolean
): void {
    const layers = isMajor ? MAJOR_GLOW_LAYERS : NORMAL_GLOW_LAYERS;
    for (const [sizeScale, alphaScale] of layers) {
        p.textSize(baseSize * sizeScale);
        p.fill(255, 255, 255, glowIntensity * alphaScale);
        p.text(text, x, y);
    }
}

/** コンボアニメーションのトリガー判定 */
function updateComboAnimation(combo: number, lastAnimatedCombo: number): void {
    const shouldTrigger =
        combo >= COMBO_ANIM_THRESHOLD &&
        combo % COMBO_ANIM_INTERVAL === 0 &&
        combo > lastAnimatedCombo;

    if (shouldTrigger) {
        setLastAnimatedCombo(combo);
        setComboAnimTimer(COMBO_ANIM_DURATION);
    }
}

// ============================================
// 描画関数（エクスポート）
// ============================================

/** スコア表示（左パネル上部） */
export function drawScoreDisplay(): void {
    const p = getP5();
    const score = getScore();
    const highScore = getHighScore();
    const wave = getWave();
    const leftCenterX = getLeftPanelCenterX();

    // ランク更新
    const newRank = calculateRank(score, wave);
    if (newRank !== getRank()) {
        setRank(newRank);
    }

    // SCORE ラベル（Share Tech Mono）
    p.textAlign(p.CENTER, p.TOP);
    p.textFont('Share Tech Mono');
    p.fill(HUD_COLORS.cyan[0], HUD_COLORS.cyan[1], HUD_COLORS.cyan[2], 179);
    p.textSize(9);
    p.text('SCORE', leftCenterX, SCORE_LABEL_Y);

    // スコア値（Orbitron、カンマ区切り）
    p.textFont('Orbitron');
    p.fill(COLOR_WHITE.r, COLOR_WHITE.g, COLOR_WHITE.b);
    p.textSize(VALUE_SIZE);

    // グロー効果
    const ctx = p.drawingContext as CanvasRenderingContext2D;
    ctx.save();
    ctx.shadowColor = 'rgba(255, 255, 255, 0.3)';
    ctx.shadowBlur = 10;
    p.text(formatScore(score), leftCenterX, SCORE_VALUE_Y);
    ctx.shadowBlur = 0;
    ctx.restore();

    // RANK 表示
    const rank = getRank();
    const rankColor = getRankColor(rank);
    p.textFont('Share Tech Mono');
    p.fill(rankColor[0], rankColor[1], rankColor[2], 102);
    p.textSize(8);
    p.text(`RANK: ${rank}`, leftCenterX, RANK_Y);

    // HI（ハイスコア）
    p.textFont('Share Tech Mono');
    p.fill(HUD_COLORS.cyan[0], HUD_COLORS.cyan[1], HUD_COLORS.cyan[2], 179);
    p.textSize(9);
    p.text('HI', leftCenterX, HI_LABEL_Y);
    p.textFont('Orbitron');
    p.fill(150, 150, 150);
    p.textSize(SMALL_VALUE_SIZE);
    p.text(formatScore(highScore), leftCenterX, HI_VALUE_Y);
}

// ステージ切り替えアニメーション定数
const STAGE_ANIM_FADE_IN = 30;    // フェードインフレーム数
const STAGE_ANIM_GLOW_PEAK = 60;  // グロー最大フレーム数

/** ステージ名 + ウェーブ表示（左パネル中段） */
export function drawWaveDisplay(): void {
    const p = getP5();
    const wave = getWave();
    const stageName = getCurrentStageCfg().name.toUpperCase();
    const leftCenterX = getLeftPanelCenterX();

    // STAGE ラベル（Share Tech Mono）
    p.textAlign(p.CENTER, p.TOP);
    p.textFont('Share Tech Mono');
    p.fill(HUD_COLORS.cyan[0], HUD_COLORS.cyan[1], HUD_COLORS.cyan[2], 179);
    p.textSize(9);
    p.text('STAGE', leftCenterX, STAGE_LABEL_Y);

    // ステージ切り替えアニメーション計算
    const stageTimer = getStageNameDisplayTimer();
    let stageAlpha = 255;
    let stageGlow = 8;
    let stageSizeBoost = 0;

    if (stageTimer > 0) {
        const elapsed = 180 - stageTimer;  // 経過フレーム
        // フェードイン（0→255）
        stageAlpha = Math.min(255, Math.round(255 * elapsed / STAGE_ANIM_FADE_IN));
        // グロー強調（ピーク後に通常値へ収束）
        if (elapsed < STAGE_ANIM_GLOW_PEAK) {
            const glowT = Math.sin(elapsed / STAGE_ANIM_GLOW_PEAK * Math.PI);
            stageGlow = 8 + 12 * glowT;
            stageSizeBoost = 2 * glowT;
        }
    }

    // ステージ名（Orbitron、パネル幅に収まるよう自動縮小）
    p.textFont('Orbitron');
    p.fill(HUD_COLORS.magenta[0], HUD_COLORS.magenta[1], HUD_COLORS.magenta[2], stageAlpha);

    const maxStageWidth = CONFIG.layout.panelWidth - 16;
    let stageSize = 14 + stageSizeBoost;
    p.textSize(stageSize);
    if (p.textWidth(stageName) > maxStageWidth) {
        stageSize = stageSize * maxStageWidth / p.textWidth(stageName);
        p.textSize(stageSize);
    }

    const ctx = p.drawingContext as CanvasRenderingContext2D;
    ctx.save();
    ctx.shadowColor = `rgba(${HUD_COLORS.magenta[0]}, ${HUD_COLORS.magenta[1]}, ${HUD_COLORS.magenta[2]}, 0.5)`;
    ctx.shadowBlur = stageGlow;
    p.text(stageName, leftCenterX, STAGE_VALUE_Y);
    ctx.shadowBlur = 0;
    ctx.restore();

    // WAVE ラベル（Share Tech Mono）
    p.textFont('Share Tech Mono');
    p.fill(COLOR_WHITE.r, COLOR_WHITE.g, COLOR_WHITE.b);
    p.textSize(LABEL_SIZE);
    p.text('WAVE', leftCenterX, WAVE_LABEL_Y);

    // WAVE 番号（Orbitron、大きめ）
    p.textFont('Orbitron');
    p.fill(HUD_COLORS.magenta[0], HUD_COLORS.magenta[1], HUD_COLORS.magenta[2]);
    ctx.save();
    ctx.shadowColor = `rgba(${HUD_COLORS.magenta[0]}, ${HUD_COLORS.magenta[1]}, ${HUD_COLORS.magenta[2]}, 0.5)`;
    ctx.shadowBlur = 10;
    p.textSize(VALUE_SIZE + 6);
    p.text(wave.toString(), leftCenterX, WAVE_VALUE_Y);
    ctx.shadowBlur = 0;
    ctx.restore();
}

/** 敵カウンター表示（WAVE表示の下） */
export function drawEnemyCounter(): void {
    const p = getP5();
    const defeated = getWaveEnemiesDefeated();
    const required = getEnemiesRequiredForWave();
    const leftCenterX = getLeftPanelCenterX();

    // ブリンキングカーソル（30フレームごとに切替）
    const cursor = p.frameCount % 60 < 30 ? '\u258C' : '　';

    p.textAlign(p.CENTER, p.TOP);
    p.textFont('Share Tech Mono');
    p.textSize(10);
    p.fill(HUD_COLORS.magenta[0], HUD_COLORS.magenta[1], HUD_COLORS.magenta[2]);
    p.text(`HOSTILE ${defeated}/${required} ${cursor}`, leftCenterX, ENEMY_COUNTER_Y);
}

/**
 * コンボ表示（右パネル中段、常時表示）
 * - 色変化: コンボ数に応じて寒色→暖色→金→虹色
 * - グロー演出: 10以上で10の倍数到達時に発光
 * - グラデーションテキスト + 倍率表示
 */
export function drawComboDisplay(): void {
    const p = getP5();
    const combo = getCombo();
    const x = getRightPanelCenterX();

    // コンボ途切れ検出
    if (combo === 0 && _prevCombo >= 5) {
        _comboBreakTimer = COMBO_BREAK_DURATION;
        _brokenComboValue = _prevCombo;
    }
    _prevCombo = combo;

    // ラベル（Share Tech Mono）
    p.textAlign(p.CENTER, p.TOP);
    p.textFont('Share Tech Mono');
    p.fill(255, 255, 255, 102);
    p.textSize(9);
    p.text('COMBO', x, COMBO_LABEL_Y);

    // コンボ途切れ演出（赤くフェードアウト）
    if (_comboBreakTimer > 0) {
        const progress = _comboBreakTimer / COMBO_BREAK_DURATION; // 1→0
        const breakAlpha = progress * 180;
        const breakScale = 1 + (1 - progress) * 0.3; // 少し拡大しながらフェード
        const breakSize = COMBO_BASE_SIZE * breakScale;

        p.textFont('Orbitron');
        p.textAlign(p.CENTER, p.TOP);
        p.fill(255, 50, 80, breakAlpha);
        p.textSize(breakSize);
        p.text(_brokenComboValue.toString(), x, COMBO_VALUE_Y - (1 - progress) * 8);

        _comboBreakTimer--;
    }

    const comboAlpha = p.min(255, 100 + combo * 15);
    const comboScale = p.min(1.5, 1 + combo * 0.008);
    const textSize = COMBO_BASE_SIZE * comboScale;
    const { r, g, b } = getComboColor(p, combo);

    // アニメーション更新
    const lastAnimatedCombo = getLastAnimatedCombo();
    updateComboAnimation(combo, lastAnimatedCombo);

    // グロー効果の描画
    const timer = getComboAnimTimer();
    p.textFont('Orbitron');
    if (timer > 0) {
        const progress = timer / COMBO_ANIM_DURATION;
        const glowIntensity = Math.sin(progress * Math.PI);
        const isMajor = lastAnimatedCombo % COMBO_ANIM_MAJOR_INTERVAL === 0;

        drawGlowEffect(p, combo.toString(), x, COMBO_VALUE_Y, textSize, glowIntensity, isMajor);
        setComboAnimTimer(timer - 1);
    }

    // メインテキスト（グラデーション描画: Canvas 2D API）
    const ctx = p.drawingContext as CanvasRenderingContext2D;
    ctx.save();

    const gradient = ctx.createLinearGradient(x, COMBO_VALUE_Y, x, COMBO_VALUE_Y + textSize);
    gradient.addColorStop(0, '#ffffff');
    gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, ${comboAlpha / 255})`);

    ctx.font = `700 ${textSize}px Orbitron`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = gradient;

    // ドロップシャドウ
    ctx.shadowColor = `rgba(${r}, ${g}, ${b}, 0.4)`;
    ctx.shadowBlur = 10;
    ctx.fillText(combo.toString(), x, COMBO_VALUE_Y);
    ctx.shadowBlur = 0;

    ctx.restore();
}
